import { ChangeDetectionStrategy, Component, HostListener, OnInit, inject, signal } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { SiaBasePdfViewerComponent } from '../../components/sia-base-pdf-viewer/sia-base-pdf-viewer';
import { SiaTranslatePipe } from '../../pipes/sia-translate.pipe';
import { FileService } from '../../services/file.service';

export interface SiaPdfViewerDialogData {
  path: string;
  showConfirmButton: boolean;
  /**
   * Se `true`, le azioni del dialog (attualmente il bottone "Conferma")
   * restano disabilitate finche l'utente non scrolla fino all'ultima pagina
   * del PDF. Ha effetto solo se `showConfirmButton === true`. Default: `false`.
   */
  enableActionsOnScrollEnd?: boolean;
}

@Component({
  selector: 'sia-pdf-viewer-dialog',
  templateUrl: './sia-pdf-viewer-dialog.html',
  styleUrls: ['./sia-pdf-viewer-dialog.css'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatIcon,
    MatIconButton,
    MatButton,
    MatProgressSpinner,
    SiaBasePdfViewerComponent,
    SiaTranslatePipe,
  ],
})
export class SiaPdfViewerDialogComponent implements OnInit {
  #dialogRef = inject<MatDialogRef<SiaPdfViewerDialogComponent, boolean | undefined>>(MatDialogRef);
  #fileService = inject(FileService);

  readonly data = signal<SiaPdfViewerDialogData>(inject(MAT_DIALOG_DATA));
  readonly fileBlob = signal<Blob | null>(null);
  readonly loading = signal<boolean>(true);
  readonly error = signal<string | null>(null);
  /**
   * Abilita il bottone "Conferma". Se `enableActionsOnScrollEnd` non e
   * attivo, parte gia a `true`; altrimenti diventa `true` solo quando il
   * viewer emette `scrolledToEnd`.
   */
  readonly canConfirm = signal<boolean>(false);

  constructor() {
    // Se il vincolo "scroll fino alla fine" non e richiesto, il bottone
    // Conferma e sempre abilitato (comportamento pre-esistente).
    if (this.data().enableActionsOnScrollEnd !== true) {
      this.canConfirm.set(true);
    }
  }

  async ngOnInit(): Promise<void> {
    try {
      const blob = await this.#fileService.getBlob(this.data().path, undefined);
      this.fileBlob.set(blob);
    } catch (err: any) {
      this.error.set(String(err?.message ?? err ?? 'Errore durante il caricamento del documento'));
    } finally {
      this.loading.set(false);
    }
  }

  onClose(): void {
    this.#dialogRef.close(false);
  }

  onConfirm(): void {
    this.#dialogRef.close(true);
  }

  onScrolledToEnd(): void {
    this.canConfirm.set(true);
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key !== 'Enter') {
      return;
    }

    // Senza bottone di conferma non c'e azione positiva da scatenare.
    if (!this.data().showConfirmButton || !this.canConfirm()) {
      return;
    }

    const target = event.target;
    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement ||
      (target instanceof HTMLElement && target.isContentEditable)
    ) {
      return;
    }

    event.preventDefault();
    this.onConfirm();
  }
}
